import { SkeletonSpinnerCentered } from '@civilgrid/ui/SkeletonLoader';
import { useParams } from '@tanstack/react-router';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type Row,
  useReactTable,
} from '@tanstack/react-table';
import type { GeoJsonProperties } from 'geojson';
import { useEffect, useMemo, useState } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { useOwnMapClicks } from '~/components2/Map/mapClickOwnership';
import { measureStatus } from '~/components2/Map/Widgets/Measure/MeasureSurface';
import { GlobalFilter, Pagination, TableV2 } from '~/components2/Table';
import type { ProjectDetailsParams } from '..';
import AntennaLegend from './AntennaLegend';
import AntennaPopup from './AntennaPopup';
import { columns, columnVisibility, pagination, sorting } from './columns';
import { antennaCategoryMap, createAntennaSearchUrl, fccTypeColorMap } from './dataMaps';
import MultipleAntennaLinkPopup, { type MultipleAntennaLink } from './MultipleAntennaLinkPopup';
import store from './store';

export function TabDetails() {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { antennaMap, selectedObjectId } = useSnapshot(store) as typeof store;
  const [combinedData, setCombinedData] = useState<GeoJSON.GeoJsonProperties[]>([]);
  const [multipleAntennaLinks, setMultipleAntennaLinks] = useState<MultipleAntennaLink[]>([]);

  useEffect(() => {
    store.fetchAntennaMap(projectId);
    return () => store.reset();
  }, [projectId]);

  useEffect(() => {
    const combinedData_ = [];
    if (antennaMap?.antennaSearchData.success) {
      const antennaData =
        antennaMap?.antennaSearchData.data.features?.map((feature) => {
          const properties = { ...feature.properties };
          properties.categoryMapped = antennaCategoryMap[properties.category] || properties.category;
          properties.source = 'AntennaSearch';
          properties.url = createAntennaSearchUrl(properties);
          if (feature.geometry.type === 'Point') {
            properties.lat = feature.geometry.coordinates[1];
            properties.lng = feature.geometry.coordinates[0];
          }
          return properties;
        }) ?? [];
      combinedData_.push(...antennaData);
    }

    if (antennaMap?.fccInfoData.success) {
      const fccData =
        antennaMap?.fccInfoData.data.features?.map((feature) => {
          const properties = {
            OBJECTID: feature.properties?.OBJECTID,
            owner: feature.properties?.Name,
            categoryMapped: feature.properties?.type,
            source: 'FCCInfo',
            lat: 0,
            lng: 0,
          };
          if (feature.geometry.type === 'Point') {
            properties.lat = feature.geometry.coordinates[1];
            properties.lng = feature.geometry.coordinates[0];
          }
          return properties;
        }) ?? [];
      combinedData_.push(...fccData);
    }

    setCombinedData(combinedData_);
  }, [antennaMap]);

  const onRowDoubleClick = (row: Row<GeoJsonProperties>) => {
    const p = row.original!;
    if (p.category === 'multipleAntenna') {
      const uniqueSystemIdentifierList = p.unique_sys.split(',');
      const callSignList = p.call_sign.split(',');
      const locationNumberList = p.location_n.split(',');
      const ownerList = p.owner.split(',');

      const linksList: MultipleAntennaLink[] = [];

      for (let i = 0; i < uniqueSystemIdentifierList.length; i++) {
        const uniqueSystemIdentifier = uniqueSystemIdentifierList[i];
        const callSign = callSignList[i];
        const locationNumber = locationNumberList[i];
        const owner = ownerList[i];
        const url = `https://www.antennasearch.com/HTML/individual/antenna.php?unique_system_identifier=${uniqueSystemIdentifier}&call_sign=${callSign}&location_number=${locationNumber}`;
        const thisAntenna = {
          url,
          owner,
          uniqueSystemIdentifier,
          callSign,
          locationNumber,
        };
        linksList.push(thisAntenna);
      }
      setMultipleAntennaLinks(linksList);
    } else {
      const url = createAntennaSearchUrl(p);
      window.open(url, '_blank');
    }
  };

  const table = useReactTable({
    data: combinedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableSorting: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getRowId: (p) => p!.OBJECTID,
    initialState: {
      columnVisibility,
      pagination,
      sorting,
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    if (selectedObjectId) {
      const row = table.getRowModel().rowsById[selectedObjectId];
      if (row) {
        table.setRowSelection({ [selectedObjectId]: true });
        const sortedRows = table.getSortedRowModel().rows;
        const currentIndex = sortedRows.findIndex((r) => r.id.toString() === selectedObjectId);

        if (currentIndex !== -1) {
          const pageIndex = Math.floor(currentIndex / table.getState().pagination.pageSize);

          table.setPageIndex(pageIndex);

          timer = setTimeout(() => {
            const rowElement = document.getElementById(`row-${row.id}`);
            if (rowElement) {
              rowElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }
          }, 0);
        }
      }
    } else {
      table.setRowSelection({});
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [selectedObjectId, table.getState().sorting]);

  if (!antennaMap) {
    return <SkeletonSpinnerCentered />;
  }

  if (!antennaMap?.antennaSearchData.success) {
    return (
      <p className="text-sm">
        Antenna data is currently being loaded for this location. You will be notified via email when completed. We
        appreciate your patience.
      </p>
    );
  }

  if (!combinedData?.length) {
    return <p className="text-sm">No antenna data found at this location.</p>;
  }

  return (
    <div className="grid grid-cols-1 gap-2 overflow-auto">
      <AntennaLegend />
      <MultipleAntennaLinkPopup
        open={multipleAntennaLinks.length > 0}
        onClose={() => setMultipleAntennaLinks([])}
        links={multipleAntennaLinks}
      />
      <div className="flex w-full items-end justify-between">
        <Pagination table={table} />
        <div>
          <GlobalFilter table={table} />
        </div>
      </div>

      <TableV2
        id="antenna-map-table"
        table={table}
        onRowDoubleClick={onRowDoubleClick}
        onRowClick={(row) => {
          const { lng, lat } = row.original!;
          store.setClickedCoordinates({ lng, lat });
        }}
        getRowClassName={(row) => {
          return row.getIsSelected() ? 'border-2 border-blue-500' : '';
        }}
      />
    </div>
  );
}

export function MapChildren() {
  // This tab uses left-click to select antenna features, so claim map-click ownership (CG-670 sidebar defers).
  useOwnMapClicks();
  const { antennaMap, clickedCoordinates } = useSnapshot(store) as typeof store;
  const [selectedFeatures, setSelectedFeatures] = useState<GeoJSON.Feature<GeoJSON.Point>[]>([]);
  const [popupLocation, setPopupLocation] = useState<[number, number] | null>(null);

  const { current: map } = MapGL.useMap();

  async function onClick(e: MapGL.MapMouseEvent) {
    if (measureStatus.isMeasuring === true) return;

    if (map) {
      const foundFeatures = map?.queryRenderedFeatures(e.point, {
        layers: ['combinedAntennaMapLayer'],
      }) as GeoJSON.Feature<GeoJSON.Point>[] | undefined;

      setSelectedFeatures(foundFeatures || []);
      setPopupLocation([e.lngLat.lng, e.lngLat.lat]);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (!map) return;
    store.setCurrentMap(map);
    map.on('click', onClick);

    return () => {
      map?.off('click', onClick);
      store.resetCurrentMap();
    };
  }, [map]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (clickedCoordinates) {
      if (!map) return;
      map.flyTo({
        center: [clickedCoordinates.lng, clickedCoordinates.lat],
        zoom: 19,
      });
    }
  }, [clickedCoordinates]);

  const combinedGeoJsonLayer = useMemo(() => {
    const combinedFeatures = [];

    if (antennaMap?.antennaSearchData.success) {
      combinedFeatures.push(...antennaMap.antennaSearchData.data.features);
    }

    if (antennaMap?.fccInfoData.success) {
      const fccFeatures = antennaMap.fccInfoData.data.features.map((feature) => ({
        ...feature,
        properties: {
          ...feature.properties,
          owner: feature.properties?.Name,
          category: feature.properties?.type,
          color: fccTypeColorMap(feature?.properties?.type || ''),
        },
      }));
      combinedFeatures.push(...fccFeatures);
    }

    const combinedData: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: combinedFeatures,
    };

    return (
      <MapGL.Source id="combinedAntennaMapSource" type="geojson" data={combinedData}>
        <MapGL.Layer
          id="combinedAntennaMapLayer"
          type="circle"
          paint={{
            'circle-radius': 3,
            'circle-color': ['get', 'color'],
          }}
        />
      </MapGL.Source>
    );
  }, [antennaMap?.antennaSearchData, antennaMap?.fccInfoData]);

  const popup = useMemo(() => {
    if (selectedFeatures.length > 0 && popupLocation) {
      return (
        <AntennaPopup
          popupLocation={popupLocation}
          selectedFeatures={selectedFeatures}
          setSelectedFeatures={setSelectedFeatures}
          setPopupLocation={setPopupLocation}
        />
      );
    }
  }, [selectedFeatures, popupLocation]);

  return (
    <>
      {combinedGeoJsonLayer}
      {popup}
    </>
  );
}
